import {Menu} from '@mantine/core';
import React, {useCallback} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import createChatForEntry from 'llm/data-capture/createChatForEntry';
import AsyncButton, {AsyncButtonLoadingBehavior} from 'ui-library/buttons/AsyncButton';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';

interface StartChatWithEntryButtonProps {
  entryId: string;
  showEditContents: boolean;
  isEditable: boolean;
  showAnalyzeData: boolean;
}

function getComposeLabel(isEditable: boolean): string {
  return isEditable ? 'Edit contents' : 'Ask questions';
}

function getComposeAgentType(isEditable: boolean): AgentType {
  const canAsk = BenchlingConfig.LLM_ENABLE_AI_DEEP_RESEARCH;
  return isEditable || !canAsk ? AgentType.DATA_CAPTURE : AgentType.ASK;
}

export default function StartChatWithEntryButton({
  entryId,
  showEditContents,
  isEditable,
  showAnalyzeData,
}: StartChatWithEntryButtonProps): JSX.Element {
  const handleCompose = useCallback(
    () => createChatForEntry(entryId, getComposeAgentType(isEditable)),
    [entryId, isEditable]
  );
  const handleAnalysis = useCallback(() => createChatForEntry(entryId, AgentType.ANALYSIS), [entryId]);

  // Single option: render a plain button
  if (showEditContents && !showAnalyzeData) {
    return (
      <AsyncButton
        buttonStyle={ButtonStyle.TERTIARY_BLUE}
        icon="sparkle"
        loadingBehavior={AsyncButtonLoadingBehavior.SPINNER_WITH_TEXT}
        onClick={handleCompose}
        size={ButtonSize.MINI}
        text={getComposeLabel(isEditable)}
      />
    );
  }
  if (showAnalyzeData && !showEditContents) {
    return (
      <AsyncButton
        buttonStyle={ButtonStyle.TERTIARY_BLUE}
        icon="lightbulb"
        loadingBehavior={AsyncButtonLoadingBehavior.SPINNER_WITH_TEXT}
        onClick={handleAnalysis}
        size={ButtonSize.MINI}
        text="Analyze data"
      />
    );
  }

  // Both options: render a dropdown
  return (
    <Menu shadow="md" position="bottom-end">
      <Menu.Target>
        <span style={{display: 'inline-block'}}>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_BLUE}
            icon="sparkle"
            isDropdown={true}
            size={ButtonSize.MINI}
            text="Work with AI"
          />
        </span>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item onClick={handleCompose}>{getComposeLabel(isEditable)}</Menu.Item>
        <Menu.Item onClick={handleAnalysis}>Analyze data</Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
